import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PopulatedEntitySuggestion } from '@bliks-io/contracts';
import { cn } from '@bliks-io/ui';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import {
  STEP_ACTION_ICONS,
  STEP_ACTION_ICON_COLORS
} from '@/components/processes/step-action-icons';

type SuggestionSource = Pick<
  PopulatedEntitySuggestion,
  'sourceProcessLabel' | 'sourceRecordingStepLabel' | 'sourceRecordingActionType'
>;

interface SuggestionSourceLabelProps {
  suggestion: SuggestionSource;
  className?: string;
  /** When set, the process row becomes a button (e.g. to open process info). */
  onProcessClick?: () => void;
  /** When set, the step row becomes a button (e.g. to preview the recording). */
  onStepClick?: () => void;
}

/**
 * A suggestion's origin as two stacked rows: the owning process (always, behind
 * the process indicator) and — when the source is a recording with a known step
 * — the step below it, behind its action-type indicator. Start-event
 * suggestions carry no step, so only the process row renders. Either row can be
 * made clickable via its handler. Shared by the pending-reviews table cell and
 * the review-modal source row.
 */
export function SuggestionSourceLabel({
  suggestion,
  className,
  onProcessClick,
  onStepClick
}: SuggestionSourceLabelProps) {
  const { t } = useTranslation();
  const ProcessIcon = ENTITY_ICONS.process;
  const processLabel = suggestion.sourceProcessLabel ?? t('myTasks.unnamedProcess');

  const stepLabel = suggestion.sourceRecordingStepLabel;
  const actionType = suggestion.sourceRecordingActionType ?? 'task';
  const StepIcon = STEP_ACTION_ICONS[actionType];

  return (
    <span className={cn('flex min-w-0 flex-col gap-1', className)}>
      <SourceRow
        icon={<ProcessIcon className="text-muted-foreground h-4 w-4 shrink-0" />}
        label={processLabel}
        onClick={onProcessClick}
      />
      {stepLabel && (
        <SourceRow
          icon={
            <StepIcon className={cn('h-4 w-4 shrink-0', STEP_ACTION_ICON_COLORS[actionType])} />
          }
          label={stepLabel}
          onClick={onStepClick}
        />
      )}
    </span>
  );
}

function SourceRow({
  icon,
  label,
  onClick
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      {icon}
      <span className="truncate">{label}</span>
    </>
  );
  if (!onClick) {
    return <span className="flex min-w-0 items-center gap-2">{content}</span>;
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        // Shield host row-click handlers (e.g. table row selection) from the
        // inner preview click.
        e.stopPropagation();
        onClick();
      }}
      className="flex min-w-0 items-center gap-2 text-left hover:underline"
    >
      {content}
    </button>
  );
}
